import React, { useState } from 'react';
import { Equal, Delete, Plus, Minus, X, Divide, History, Calculator } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

function BasicCalculator() {
  const [display, setDisplay] = useState<string>('0');
  const [history, setHistory] = useState<string[]>([]);
  const [showHistory, setShowHistory] = useState<boolean>(false);

  const handleNumber = (num: string) => {
    setDisplay(prev => prev === '0' ? num : prev + num);
  };

  const handleOperator = (op: string) => {
    setDisplay(prev => prev.endsWith(' ') ? prev.slice(0, -3) + ` ${op} ` : prev + ` ${op} `);
  };

  const calculate = () => {
    try {
      const result = eval(display.replace(/×/g, '*').replace(/÷/g, '/'));
      setHistory(prev => [...prev, `${display} = ${result}`]);
      setDisplay(result.toString());
    } catch (error) {
      setDisplay('Greška');
    }
  };

  const clear = () => {
    setDisplay('0');
  };

  const deleteLastChar = () => {
    setDisplay(prev => {
      if (prev.length === 1) return '0';
      if (prev.endsWith(' ')) return prev.slice(0, -3);
      return prev.slice(0, -1);
    });
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Online Kalkulator",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Besplatni online kalkulator s poviješću računanja. Jednostavan za korištenje, precizan i brz.",
    "url": "https://online-kalkulator.com",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Osnovne matematičke operacije",
      "Povijest računanja",
      "Jasno i pregledno sučelje",
      "Besplatno korištenje",
      "Radi na svim uređajima"
    ]
  };

  return (
    <>
      <SEO 
        title="Online Kalkulator | Besplatni Digitalni Kalkulator"
        description="Besplatni online kalkulator s poviješću računanja. Jednostavan za korištenje, precizan i brz. Idealan za svakodnevne matematičke operacije."
        canonicalUrl="/"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calculator className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Online Kalkulator</h1>
        </div>
        
        <div className="max-w-md mx-auto mb-12">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Calculator Display */}
            <div className="p-6 bg-gray-900 text-right">
              <p className="text-gray-400 text-sm mb-2">
                {history[history.length - 1] || '\u00A0'}
              </p>
              <p className="text-white text-4xl font-light tracking-wider break-all">
                {display}
              </p>
            </div>

            {/* Calculator Buttons */}
            <div className="grid grid-cols-4 gap-1 p-3 bg-gray-100">
              <button onClick={clear} 
                className="col-span-2 p-4 text-red-600 hover:bg-gray-200 rounded-lg transition">
                AC
              </button>
              <button onClick={deleteLastChar}
                className="p-4 text-blue-600 hover:bg-gray-200 rounded-lg transition">
                <Delete className="w-6 h-6 mx-auto" />
              </button>
              <button onClick={() => handleOperator('÷')}
                className="p-4 text-blue-600 hover:bg-gray-200 rounded-lg transition">
                <Divide className="w-6 h-6 mx-auto" />
              </button>

              {[7, 8, 9].map(num => (
                <button key={num} onClick={() => handleNumber(num.toString())}
                  className="p-4 hover:bg-gray-200 rounded-lg transition">
                  {num}
                </button>
              ))}
              <button onClick={() => handleOperator('×')}
                className="p-4 text-blue-600 hover:bg-gray-200 rounded-lg transition">
                <X className="w-6 h-6 mx-auto" />
              </button>

              {[4, 5, 6].map(num => (
                <button key={num} onClick={() => handleNumber(num.toString())}
                  className="p-4 hover:bg-gray-200 rounded-lg transition">
                  {num}
                </button>
              ))}
              <button onClick={() => handleOperator('-')}
                className="p-4 text-blue-600 hover:bg-gray-200 rounded-lg transition">
                <Minus className="w-6 h-6 mx-auto" />
              </button>

              {[1, 2, 3].map(num => (
                <button key={num} onClick={() => handleNumber(num.toString())}
                  className="p-4 hover:bg-gray-200 rounded-lg transition">
                  {num}
                </button>
              ))}
              <button onClick={() => handleOperator('+')}
                className="p-4 text-blue-600 hover:bg-gray-200 rounded-lg transition">
                <Plus className="w-6 h-6 mx-auto" />
              </button>

              <button onClick={() => setShowHistory(!showHistory)}
                className="p-4 hover:bg-gray-200 rounded-lg transition">
                <History className="w-6 h-6 mx-auto" />
              </button>
              <button onClick={() => handleNumber('0')}
                className="p-4 hover:bg-gray-200 rounded-lg transition">
                0
              </button>
              <button onClick={() => handleNumber('.')}
                className="p-4 hover:bg-gray-200 rounded-lg transition">
                .
              </button>
              <button onClick={calculate}
                className="p-4 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition">
                <Equal className="w-6 h-6 mx-auto" />
              </button>
            </div>
          </div>

          {/* History Panel */}
          {showHistory && (
            <div className="mt-4 bg-white rounded-xl shadow-lg p-4">
              <h3 className="text-lg font-semibold mb-2">Povijest računanja</h3>
              {history.length === 0 ? (
                <p className="text-gray-500">Nema povijesti računanja</p>
              ) : (
                <ul className="space-y-2">
                  {history.map((calc, index) => (
                    <li key={index} className="text-gray-600 border-b pb-2">
                      {calc}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Information Section */}
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl mx-auto">
          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru</h2>
              <p className="text-gray-600 leading-relaxed">
                Dobrodošli u naš besplatni online kalkulator! Ovo je jednostavan ali moćan alat za sve vaše matematičke potrebe. 
                Dizajniran je da bude intuitivan i lak za korištenje, bez potrebe za instalacijom ili registracijom.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-700 mb-4">Značajke</h2>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Osnovne matematičke operacije (zbrajanje, oduzimanje, množenje, dijeljenje)</li>
                <li>Povijest računanja za praćenje prethodnih izračuna</li>
                <li>Jasno i pregledno sučelje</li>
                <li>Potpuno besplatno korištenje</li>
                <li>Radi na svim uređajima</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
              <ol className="list-decimal list-inside text-gray-600 space-y-2">
                <li>Unesite brojeve koristeći tipkovnicu na ekranu</li>
                <li>Odaberite željenu matematičku operaciju</li>
                <li>Pritisnite = za rezultat</li>
                <li>Koristite AC za brisanje ili povijest za pregled prethodnih izračuna</li>
              </ol>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-700 mb-4">Povezani Kalkulatori</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link 
                  to="/pdv-kalkulator"
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                >
                  <h3 className="font-medium text-gray-800 mb-1">PDV Kalkulator</h3>
                  <p className="text-sm text-gray-600">Izračunajte PDV i ukupne iznose</p>
                </Link>
                <Link 
                  to="/izracun-postotka"
                  className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                >
                  <h3 className="font-medium text-gray-800 mb-1">Izračun Postotka</h3>
                  <p className="text-sm text-gray-600">Izračunajte postotke i omjere</p>
                </Link>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}

export default BasicCalculator;